// 📄 nexusChatService.ts
// Conversaciones de Nexus AI (N3, 2026-10-27). Antes había UNA sola
// conversación fija (usuarios/{uid}/chats/general) y "Nueva conversación"
// la borraba. Ahora cada conversación es un doc usuarios/{uid}/chats/{chatId}
// con título y fecha, y sus mensajes siguen en .../messages (el trigger
// onebotGemini ya escuchaba cualquier chatId). Privado del usuario: lo cubre
// la regla usuarios/{uid}/{document=**} de firestore.rules.
import {
  collection, doc, addDoc, setDoc, getDoc, getDocs, updateDoc, writeBatch, query, orderBy, limit,
  onSnapshot, serverTimestamp, Unsubscribe
} from 'firebase/firestore';
import { db } from '../firebase';

export const CHAT_LEGADO = 'general';
const MAX_CONVERSACIONES = 40;

export interface ConversacionNexus {
  id: string;
  titulo: string;
  proyectoId: string;
  proyectoNombre: string;
  creadoEn: any;
  actualizadoEn: any;
}

const chatsRef = (uid: string) => collection(db, 'usuarios', uid, 'chats');
export const mensajesRef = (uid: string, chatId: string) => collection(db, 'usuarios', uid, 'chats', chatId, 'messages');
export const mensajeRef = (uid: string, chatId: string, msgId: string) => doc(db, 'usuarios', uid, 'chats', chatId, 'messages', msgId);

/** Título automático: la primera pregunta, corta y sin cortar palabras. */
export const tituloDesdePregunta = (texto: string): string => {
  const limpio = texto.replace(/\s+/g, ' ').trim().replace(/^[¿¡]+/, '');
  const base = limpio.length <= 60 ? limpio : `${limpio.slice(0, 60).replace(/\s+\S*$/, '')}…`;
  const conSigno = texto.trim().startsWith('¿') ? `¿${base}` : base;
  return conSigno.charAt(0).toUpperCase() + conSigno.slice(1) || 'Conversación';
};

export const escucharConversaciones = (uid: string, cb: (lista: ConversacionNexus[]) => void): Unsubscribe =>
  onSnapshot(query(chatsRef(uid), orderBy('actualizadoEn', 'desc'), limit(MAX_CONVERSACIONES)), (snap) => {
    cb(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<ConversacionNexus, 'id'>) })));
  });

/**
 * La conversación única de antes ("general") no tenía doc propio, así que no
 * aparecería en la lista: si tiene mensajes, se le crea el doc una vez.
 */
export const migrarConversacionLegado = async (uid: string): Promise<void> => {
  const ref = doc(db, 'usuarios', uid, 'chats', CHAT_LEGADO);
  if ((await getDoc(ref)).exists()) return;
  const msgs = await getDocs(query(mensajesRef(uid, CHAT_LEGADO), orderBy('createTime', 'desc'), limit(1)));
  if (msgs.empty) return;
  const ultimo = msgs.docs[0].data().createTime || serverTimestamp();
  await setDoc(ref, { titulo: 'Conversación anterior', proyectoId: '', proyectoNombre: '', creadoEn: ultimo, actualizadoEn: ultimo });
};

export const crearConversacion = async (uid: string, primeraPregunta: string, proyecto: { id: string; nombre: string } | null): Promise<string> => {
  const ref = await addDoc(chatsRef(uid), {
    titulo: tituloDesdePregunta(primeraPregunta),
    proyectoId: proyecto?.id || '',
    proyectoNombre: proyecto?.nombre || '',
    creadoEn: serverTimestamp(),
    actualizadoEn: serverTimestamp()
  });
  return ref.id;
};

export const tocarConversacion = (uid: string, chatId: string) =>
  setDoc(doc(db, 'usuarios', uid, 'chats', chatId), { actualizadoEn: serverTimestamp() }, { merge: true });

export const renombrarConversacion = (uid: string, chatId: string, titulo: string) =>
  updateDoc(doc(db, 'usuarios', uid, 'chats', chatId), { titulo: titulo.trim().slice(0, 80) || 'Conversación' });

/** Borra la conversación y sus mensajes (en lotes de 400). */
export const borrarConversacion = async (uid: string, chatId: string): Promise<void> => {
  const snap = await getDocs(mensajesRef(uid, chatId));
  for (let i = 0; i < snap.docs.length; i += 400) {
    const batch = writeBatch(db);
    snap.docs.slice(i, i + 400).forEach((d) => batch.delete(d.ref));
    await batch.commit();
  }
  const batch = writeBatch(db);
  batch.delete(doc(db, 'usuarios', uid, 'chats', chatId));
  await batch.commit();
};
